import { useEffect } from 'react';
import { buildThresholds } from './utils';

/**
 * @deprecated use `hooks/useEnteredView` or `hooks/useIntersectionObserver` instead!
 *
 * Observes a target element and fires a callback function that is executed whenever the element enters the viewport,
 * or when the amount by which the viewport intersects with the element changes by a requested threshold.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API Intersection Observer API
 *
 * @param {Object} config
 * @param {Object} config.observableRef - The observed target reference.
 * @param {Function} [config.onObserve] - A callback to be fired when the element enters the viewport by the requested threshold.
 * @param {Function} [config.onIntersect] - A callback to be fired when the element enters the viewport.
 * @param {Function} [config.onNotSupported] - A callback to be fired when the element enters the viewport by the requested threshold.
 * @param {number} [config.intersectionThreshold] - The intersection threshold required for the viewport to intersect with the observed target for an observation callback to be fired. The value should be between 0 and 1.
 * @param {number} [config.thresholdSteps] - The intersection threshold steps granularity in which the observation listener will be configured to.

 * @returns {Object} The observed target reference.
 */
const useObserver = ({
    observableRef = null,
    onObserve = Function.prototype,
    onIntersect = Function.prototype,
    onNotSupported = Function.prototype,
    intersectionThreshold = 0,
    thresholdSteps = 5
}) => {
    useEffect(() => {
        const observe = () => {
            if (!(window && window.IntersectionObserver)) {
                onNotSupported();
                return;
            }

            if (!observableRef) {
                throw new Error('observableRef is not defined');
            }

            const observedTarget = observableRef.current;

            const observer = initObserver(observedTarget);

            observedTarget && observer.observe(observedTarget);

            return () => observer.disconnect();
        };

        const initObserver = (observedTarget) => {
            const options = {
                threshold: buildThresholds(thresholdSteps)
            };

            const observer = new IntersectionObserver((entries) => {
                const [{ intersectionRatio }] = entries;
                const isInViewport = intersectionRatio > 0;
                const hasPassedThreshold = intersectionRatio >= intersectionThreshold;

                isInViewport && onIntersect({ intersectionRatio });

                if (!isInViewport || !hasPassedThreshold) {return;}

                onObserve({ intersectionRatio });

                observer.unobserve(observedTarget);
            }, options);

            return observer;
        };

        return observe();
    }, [observableRef, onObserve, onIntersect, onNotSupported, intersectionThreshold, thresholdSteps]);

    return observableRef;
};

export default useObserver;
